import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NgIf } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Toast } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';
import { XdsButtonComponent } from 'src/app/shared/ui/button/button.component';

/**
 * ngx-toastr-Toast mit übersetztem Schließen-Knopf.
 *
 * Die Bibliothek (`Toast`, ngx-toastr 19) hat `aria-label="Close"` fest im Template — bis zum
 * 11.10.2026 hieß der Schließen-Knopf jedes Toasts deshalb in jeder Sprache „Close"
 * (Screenreader, Barrierefreiheitsbaum). Template und Animation sind 1:1 die der Bibliothek,
 * nur das Label läuft über `CLOSE`, dazu ein optionaler Aktionsknopf (`payload.actionLabel`). Eingebunden über `ToastrModule.forRoot({ toastComponent })`
 * in `main.ts`; Verhalten (Timer, Tap, Fortschritt) erbt die Klasse unverändert.
 */
@Component({
  // Attribut-Selektor wie im Original (`[toast-component]`): das Overlay legt den Host dann als
  // `<div>` an. Ein Element-Selektor ergäbe ein Inline-Element, an dem `width`/Polster aus dem
  // Toastr-CSS nicht greifen.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[translated-toast-component]',
  standalone: true,
  imports: [NgIf, TranslateModule, XdsButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
  template: `
    <button
      *ngIf="options.closeButton"
      (click)="remove()"
      type="button"
      class="toast-close-button"
      [attr.aria-label]="'CLOSE' | translate"
      [attr.title]="'CLOSE' | translate"
    >
      <span aria-hidden="true">&times;</span>
    </button>
    <div *ngIf="title" [class]="options.titleClass" [attr.aria-label]="title">
      {{ title }} <ng-container *ngIf="duplicatesCount">[{{ duplicatesCount + 1 }}]</ng-container>
    </div>
    <div *ngIf="message && options.enableHtml" role="alert" [class]="options.messageClass" [innerHTML]="message"></div>
    <div *ngIf="message && !options.enableHtml" role="alert" [class]="options.messageClass" [attr.aria-label]="message">
      {{ message }}
    </div>
    <xds-button *ngIf="actionLabel" variant="ghost" size="sm" class="toast-action" (clicked)="runAction($event)">
      {{ actionLabel }}
    </xds-button>
    <div *ngIf="options.progressBar">
      <div class="toast-progress" [style.width]="width() + '%'"></div>
    </div>
  `,
  styles: [
    `
      .toast-action {
        display: inline-block;
        margin-top: 6px;
      }
    `,
  ],
})
export class TranslatedToastComponent extends Toast {
  /**
   * Optionale Aktion (z. B. „Öffnen“ beim Push im Vordergrund): `payload: { actionLabel }` beim
   * `toastr.*`-Aufruf, Reaktion über `ActiveToast.onAction`. Der Klick löst nicht zusätzlich `onTap` aus.
   */
  get actionLabel(): string | null {
    const label = (this.options?.payload as { actionLabel?: unknown } | undefined)?.actionLabel;
    return typeof label === 'string' && label ? label : null;
  }

  runAction(event: Event): void {
    event.stopPropagation();
    this.toastPackage.triggerAction();
    this.remove();
  }
}
